import { Component, Vue, Watch } from 'vue-property-decorator';
import { addDays, addMinutes, addMonths, addWeeks, differenceInMinutes, endOfDay, endOfMonth, endOfWeek, format, isAfter, isBefore, isEqual, isWithinInterval, parseISO, setHours, setMinutes, startOfDay, startOfMonth, startOfWeek, subDays, subMonths, subWeeks } from 'date-fns';
import { find, findIndex, sortBy } from 'lodash';
import CalendarEvent from '../calendar-event/index.vue';
import CalendarEventInterface from '@/model/vo/calendar/calendar-event-interface';
import CalendarJob from '@/model/entity/calendar/calendar-job';
import CalendarJobComponent from '../calendar-job/index.vue';
import CalendarJobDto from '@/model/entity/calendar/calendar-job-dto';
import EventBus from '@vertical-plus/vue-js-core/dist/util/event/event-bus';
import FormFieldValuesInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-values-interface';
import FormInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-interface';
import JobCard from '../job-card/index.vue';
import Timesheet from '@/model/entity/audit/timesheet';
import User from '@vertical-plus/vue-js-micro-service-components/lib/model/entity/organisation/user';
import draggable from 'vuedraggable';

@Component({
    components:
    {
        'app-calendar-event': CalendarEvent,
        'app-calendar-job': CalendarJobComponent,
        'app-job-card': JobCard,
        draggable,
    },
})
export default class Calendar extends Vue
{
    private static TYPE_DAY = 'day';
    private static TYPE_WEEK = 'week';
    private static TYPE_MONTH = 'month';

    private selectedUser: User|null = null;
    private showWeekends = false;

    private date: Date = new Date();

    private calendarJobs: CalendarJob[] = [];
    private timesheets: Timesheet[] = [];

    private workingDayStartHour = 9;
    private workingDayStartMinute = 0;
    private workingDayEndHour = 17;
    private workingDayEndMinute = 30;

    private previousUserId: string|null = null;
    private previousDateFrom: Date|null = null;
    private previousDateTo: Date|null = null;

    private form: FormInterface|null = null;

    $refs!:
    {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form: any,
    };

    created()
    {
        EventBus.instance.$on('calendar-job-drop-deleted', (calendarJobId: number, jobId: number) =>
        {
            const calendarJobIndex = findIndex(this.calendarJobs, { id: calendarJobId });
            if (calendarJobIndex > -1)
            {
                const calendarJob = this.calendarJobs[calendarJobIndex];
                this.calendarJobs.splice(calendarJobIndex, 1);

                EventBus.instance.$emit('calendar-job-deleted', calendarJob);
            }
        });

        this.generateForm();
    }

    private mounted()
    {
        this.initUser();
    }

    @Watch('date')
    private onDateChanged()
    {
        // Reload events
        this.loadEvents();
    }

    /**
     * Returns the calendar type
     */

    private get type(): 'day'|'week'|'month'
    {
        return this.$store.getters['app/calendarType'];
    }

    /**
     * Sets the calendar type
     */

    private set type(value: string)
    {
        this.$store.commit('app/calendarType', value);

        this.loadEvents();
    }

    /**
     * Returns whether to show timesheet entries in the past
     */

    private get showTimesheets(): boolean
    {
        return this.$store.getters['app/calendarTimesheets'];
    }

    /**
     * Sets whether to show timesheet entries in the past
     */

    private set showTimesheets(value: boolean)
    {
        this.$store.commit('app/calendarTimesheets', value);

        this.loadTimesheets();
    }

    private displayEvents: CalendarEventInterface[] =
        [
            {
                start: new Date('1900-01-01'),
                end: new Date('1900-01-01'),
                name: '',
            },
        ];

    @Watch('type')
    private onTypeChanged()
    {
        this.onEventsChanged();
    }

    /**
     * Regenerates the displayEvents depending on the view mode
     */

    @Watch('events', {
        deep: true,
        immediate: true,
    })
    private onEventsChanged()
    {
        // Empty the array
        this.displayEvents.splice(0, this.displayEvents.length);

        // If not month view add in the events
        if (this.type !== 'month')
        {
            this.displayEvents.splice(
                0, this.displayEvents.length, ...this.events,
            );
        }

        // Weird bug fix for initial empty array
        if (this.displayEvents.length === 0)
        {
            this.displayEvents.push({
                start: new Date('1900-01-01'),
                end: new Date('1900-01-01'),
                name: '',
            });
        }
    }

    /**
     * Returns the visible events
     */

    private get events(): CalendarEventInterface[]
    {
        const events: CalendarEventInterface[] = [];
        const hour = setMinutes(this.$clearSeconds(new Date()), 0);

        // Add jobs
        for (const calendarJob of this.calendarJobs)
        {
            // If showing timesheets don't show before the current hour, except on month view
            if (this.showTimesheets && this.type !== Calendar.TYPE_MONTH && isBefore(calendarJob.dateTo, hour))
            {
                continue;
            }

            if (!calendarJob.job)
            {
                continue;
            }

            events.push({
                job: calendarJob.job,
                calendarJob,
                start: calendarJob.dateFrom,
                end: calendarJob.dateTo,
            });
        }

        // Add timesheet events - only if on day / week view
        if (this.showTimesheets && this.type !== Calendar.TYPE_MONTH)
        {
            for (const timesheet of this.timesheets)
            {
                if (!timesheet.dateTo || !timesheet.dateFrom)
                {
                    continue;
                }

                // Hide jobs less than 15 minutes
                const minutes = differenceInMinutes(timesheet.dateTo, timesheet.dateFrom);
                if (minutes < 15)
                {
                    continue;
                }

                events.push({
                    job: timesheet.job ?? undefined,
                    name: this.$titleCase(timesheet.task),
                    start: timesheet.dateFrom,
                    end: timesheet.dateTo,
                });
            }
        }

        // Weird bug fix for initial empty array
        if (events.length === 0)
        {
            events.push({
                start: new Date('1900-01-01'),
                end: new Date('1900-01-01'),
                name: '',
            });
        }

        return events;
    }

    /**
     * Returns events that fall on the given day
     *
     * @param day
     */

    private getMonthDayEvents(day: string): CalendarEventInterface[]
    {
        // Extract only events for the passed day
        let events: CalendarEventInterface[] = [];

        for (const event of this.events)
        {
            if (!this.getEventIsOnDay(event, day))
            {
                continue;
            }

            events.push(event);
        }

        // Sort events by start time
        events = sortBy(events, [ event => event.start.getTime() ]);

        return events;
    }

    /**
     * Returns whether the given event falls on the passed day
     *
     * @param event
     * @param day
     */

    private getEventIsOnDay(event: CalendarEventInterface, day: string): boolean
    {
        const start = startOfDay(parseISO(day));
        const end = endOfDay(start);

        const eventStart = event.start;

        if ((isEqual(eventStart, start) || isAfter(eventStart, start)) && isBefore(eventStart, end))
        {
            return true;
        }
        else
        {
            return false;
        }
    }

    /**
     * Returns the selected day
     */

    private get day(): string
    {
        return format(this.date, 'yyyy-MM-dd');
    }

    /**
     * Sets the selected day
     */

    private set day(day: string)
    {
        this.date = parseISO(day);
    }

    /**
     * Returns the weekdays to show on the calendar
     */

    private get weekdays(): number[]
    {
        return this.showWeekends ? [
            0, 1, 2, 3, 4, 5, 6,
        ] : [
            1, 2, 3, 4, 5,
        ];
    }

    /**
     * Returns the type select items
     */

    private get typeItems(): any[]
    {
        return [
            {
                value: Calendar.TYPE_MONTH,
                text: 'Month',
            },
            {
                value: Calendar.TYPE_WEEK,
                text: 'Week',
            },
            {
                value: Calendar.TYPE_DAY,
                text: 'Day',
            },
        ];
    }

    /**
     * Returns the date text to display on the page
     */

    private get dateText(): string
    {
        switch (this.type)
        {
        case Calendar.TYPE_DAY:
            return format(this.date, 'do MMMM yyyy');

        case Calendar.TYPE_MONTH:
        case Calendar.TYPE_WEEK:
        default:
            return format(this.date, 'MMMM yyyy');
        }
    }

    /**
     * Returns the from date for an event lookup
     */

    private get dateFrom(): Date
    {
        switch (this.type)
        {
        case Calendar.TYPE_DAY:
            return startOfDay(this.date);

        case Calendar.TYPE_WEEK:
            return startOfWeek(this.date, { weekStartsOn: this.showWeekends ? 0 : 1 });

        case Calendar.TYPE_MONTH:
        default:
            return startOfMonth(this.date);
        }
    }

    /**
     * Returns the to date for an event lookup
     */

    private get dateTo(): Date
    {
        switch (this.type)
        {
        case Calendar.TYPE_DAY:
            return endOfDay(this.date);

        case Calendar.TYPE_WEEK:
            return endOfWeek(this.date, { weekStartsOn: this.showWeekends ? 0 : 1 });

        case Calendar.TYPE_MONTH:
        default:
            return endOfMonth(this.date);
        }
    }

    /**
     * Inits the currently selected user
     */

    private async initUser()
    {
        if ('user_id' in this.$route.query)
        {
            const userId = this.$route.query.user_id.toString();
            this.selectedUser = await this.$serviceContainer.userService.loadUser(userId);
        }
        else
        {
            this.selectedUser = this.$user;
        }

        this.$refs.form.setValue('user', this.selectedUser?.id ?? null);

        this.loadEvents();
    }

    /**
     * Loads events into the calendar
     *
     * @param forceUpdate
     */

    private async loadEvents(forceUpdate = false)
    {
        if (!this.selectedUser)
        {
            return;
        }

        // Check to see if we need to load in new jobs or re-use the previous search
        let fromInRange: boolean;
        let toInRange: boolean;

        if (!this.previousDateFrom || !this.previousDateTo)
        {
            fromInRange = false;
            toInRange = false;
        }
        else
        {
            fromInRange = isWithinInterval(this.dateFrom, {
                start: this.previousDateFrom,
                end: this.previousDateTo,
            });

            toInRange = isWithinInterval(this.dateTo, {
                start: this.previousDateFrom,
                end: this.previousDateTo,
            });
        }

        if (!forceUpdate && fromInRange && toInRange && this.selectedUser?.id === this.previousUserId)
        {
            return;
        }

        this.previousUserId = this.selectedUser.id;
        this.previousDateFrom = this.dateFrom;
        this.previousDateTo = this.dateTo;

        // Clear the arrays to instantly update the calendar
        this.calendarJobs = [];
        this.timesheets = [];

        // Load in the new events
        await this.loadCalendarJobs();
        await this.loadTimesheets();
    }

    /**
     * Loads calendar jobs and adds them as events
     */

    private async loadCalendarJobs()
    {
        if (!this.selectedUser)
        {
            return;
        }

        const results = await this.$serviceContainer.calendarService.findCalendarJobs({
            'user': this.selectedUser.id,
            'dateFrom[after]': format(this.dateFrom, 'yyyy-MM-dd'),
            'dateFrom[strictly_before]': format(addDays(this.dateTo, 1), 'yyyy-MM-dd'),
            'itemsPerPage': 1000,
            'order': [
                {
                    key: 'dateFrom',
                    descending: false,
                },
            ],
        });

        this.calendarJobs = results.results;
    }

    /**
     * Loads the timesheets for the selected user
     */

    private async loadTimesheets()
    {
        if (!this.selectedUser || !this.showTimesheets)
        {
            this.timesheets = [];
            return;
        }

        // Load from before the current hour
        const before = setMinutes(this.$clearSeconds(new Date()), 0);

        const results = await this.$serviceContainer.timesheetService.findTimesheets({
            'user': this.selectedUser.id,
            'dateFrom[after]': format(this.dateFrom, 'yyyy-MM-dd'),
            'dateFrom[strictly_before]': format(isAfter(this.dateTo, before) ? before : this.dateTo, 'yyyy-MM-dd'),
            'itemsPerPage': 1000,
        });

        this.timesheets = results.results;
    }

    /**
     * Sets the day value to today
     */

    private onSetToday()
    {
        this.date = new Date();
    }

    /**
     * Sets the day value to the previous day
     */

    private onPrevious()
    {
        switch (this.type)
        {
        case Calendar.TYPE_MONTH:
            this.date = subMonths(this.date, 1);
            break;

        case Calendar.TYPE_WEEK:
            this.date = subWeeks(this.date, 1);
            break;

        case Calendar.TYPE_DAY:
            this.date = subDays(this.date, 1);

            for (let i = 0; i < 6; i++)
            {
                const dayOfWeek = parseInt(format(this.date, 'i'));

                if (this.weekdays.includes(dayOfWeek))
                {
                    break;
                }

                this.date = subDays(this.date, 1);
            }
            break;
        }
    }

    /**
     * Sets the day value to the next day
     */

    private onNext()
    {
        switch (this.type)
        {
        case Calendar.TYPE_MONTH:
            this.date = addMonths(this.date, 1);
            break;

        case Calendar.TYPE_WEEK:
            this.date = addWeeks(this.date, 1);
            break;

        case Calendar.TYPE_DAY:
            this.date = addDays(this.date, 1);

            for (let i = 0; i < 6; i++)
            {
                const dayOfWeek = parseInt(format(this.date, 'i'));

                if (this.weekdays.includes(dayOfWeek))
                {
                    break;
                }

                this.date = addDays(this.date, 1);
            }
            break;
        }
    }

    /**
     * Handles a job being dropped
     *
     * @param date
     * @param time
     * @param event
     */

    private async onJobDropped(
        date: string, time: string, event: any,
    )
    {
        // If this is already a calendar job, then update the job
        if (event.item.dataset.calendarJobId)
        {
            // Get the calendar job id
            const calendarJobId = parseInt(event.item.dataset.calendarJobId);

            // Find the relevant calendar job
            const calendarJob = find(this.calendarJobs, { id: calendarJobId });
            if (!calendarJob)
            {
                return;
            }

            // Update the calendar job
            const originalMinutesDifference = differenceInMinutes(calendarJob.dateTo, calendarJob.dateFrom);

            if (this.type === Calendar.TYPE_MONTH)
            {
                calendarJob.dateFrom = this.getNextMonthDayStartTime(date, event.newIndex);
            }
            else
            {
                calendarJob.dateFrom = parseISO(`${ date } ${ time }`);
            }

            calendarJob.dateTo = addMinutes(calendarJob.dateFrom, originalMinutesDifference);

            const updatedCalendarJob = await this.$serviceContainer.calendarService.updateCalendarJob(calendarJob);

            // Move the item to the correct container
            if (event.clone.parentNode)
            {
                event.clone.parentNode.appendChild(event.item);
            }

            // Emit an event for listeners
            EventBus.instance.$emit('calendar-job-updated', updatedCalendarJob);

            // If in month view, shift any later, overlapping, calendar jobs for the event day
            if (this.type === Calendar.TYPE_MONTH)
            {
                this.shiftCalendarJobs(updatedCalendarJob);
            }
        }

        // Otherwise create a new calendar job
        else
        {
            // Get the job id
            const jobId = parseInt(event.item.dataset.jobId);

            // Create a new calendar job
            const calendarJob = new CalendarJob(new CalendarJobDto());
            calendarJob.jobId = jobId;
            calendarJob.userId = this.selectedUser?.id ?? null;

            // If adding from month view then the jobs should be set to begin after the most recent one in the calendar
            if (this.type === Calendar.TYPE_MONTH)
            {
                calendarJob.dateFrom = this.getNextMonthDayStartTime(date, event.newIndex);
            }

            // Otherwise add at the specified time
            else
            {
                calendarJob.dateFrom = parseISO(`${ date } ${ time }`);
            }

            // Default to 30 minute jobs
            calendarJob.dateTo = addMinutes(calendarJob.dateFrom, 30);

            // Create the calendar job
            const createdCalendarJob = await this.$serviceContainer.calendarService.createCalendarJob(calendarJob);

            // Update the local jobs array
            this.calendarJobs.push(createdCalendarJob);

            // Move the item to the correct container
            event.clone.parentNode.insertBefore(event.item, event.clone);

            // Emit an event for listeners
            EventBus.instance.$emit('calendar-job-created', createdCalendarJob);

            // If in month view, shift any later, overlapping, calendar jobs for the event day
            if (this.type === Calendar.TYPE_MONTH)
            {
                this.shiftCalendarJobs(createdCalendarJob);
            }
        }

        // Remove the clone
        event.clone.remove();
    }

    /**
     * Returns the next start time for a calendar job on a given day
     *
     * @param date
     * @param index
     */

    private getNextMonthDayStartTime(date: string, index: number)
    {
        let start: Date;

        if (index > 0)
        {
            const events = this.getMonthDayEvents(date);
            const previousEvent = events[index - 1];

            start = previousEvent.end;
        }
        else
        {
            start = setHours(setMinutes(this.$clearSeconds(parseISO(date)), this.workingDayStartMinute), this.workingDayStartHour);
        }

        return start;
    }

    /**
     * Shifts any overlapping calendar jobs on the same day as the passed job
     *
     * @param calendarJob
     */

    private async shiftCalendarJobs(calendarJob: CalendarJob)
    {
        const events = this.getMonthDayEvents(format(calendarJob.dateFrom, 'yyyy-MM-dd'));
        let start = new Date(calendarJob.dateFrom.getTime());
        let end = new Date(calendarJob.dateTo.getTime());

        for (const event of events)
        {
            // Ignore non calendar job events or the passed calendar job
            if (!event.calendarJob || event.calendarJob.id === calendarJob.id)
            {
                continue;
            }

            // If this calendar job overlaps the current start/end push it back on the calendar
            if (isEqual(event.calendarJob.dateFrom, start) || (isAfter(event.calendarJob.dateFrom, start) && isBefore(event.calendarJob.dateFrom, end)))
            {
                const minutes = differenceInMinutes(event.calendarJob.dateTo, event.calendarJob.dateFrom);

                event.calendarJob.dateFrom = new Date(end.getTime());
                event.calendarJob.dateTo = addMinutes(end, minutes);

                this.$serviceContainer.calendarService.updateCalendarJob(event.calendarJob);
                EventBus.instance.$emit('calendar-job-updated', event.calendarJob);

                start = new Date(event.calendarJob.dateFrom.getTime());
                end = new Date(event.calendarJob.dateTo.getTime());
            }
        }
    }

    /**
     * Removes a job from the calendar
     *
     * @param calendarJob
     * @param event
     */

    private onRemoveJob(calendarJob: CalendarJob, event: any)
    {
        event.preventDefault();

        const index = findIndex(this.calendarJobs, { id: calendarJob.id });
        if (index > -1)
        {
            this.calendarJobs.splice(index, 1);
        }

        if (calendarJob.id)
        {
            this.$serviceContainer.calendarService.deleteCalendarJob(calendarJob.id);
        }

        // Emit an event for listeners
        EventBus.instance.$emit('calendar-job-deleted', calendarJob);
    }

    /**
     * Opens a google calendar event in a new tab.
     *
     * @param link
     */

    private openExternalEvent(link: string)
    {
        if (!link)
        {
            return;
        }

        const tab = window.open(link, '_blank');

        if (tab)
        {
            tab.focus();
        }
    }

    /**
     * Generates the form
     *
     * @returns
     */

    private generateForm()
    {
        const form: FormInterface =
        {
            items: [
                {
                    key: 'timesheets',
                    type: 'boolean',
                    title: this.$t('navbar.message.timesheets').toString(),
                    value: this.showTimesheets,
                    required: true,
                },
                {
                    key: 'user',
                    type: 'string',
                    title: this.$t('general.field.user').toString(),
                    hideTitle: true,
                    value: this.selectedUser?.id ?? null,
                    required: true,
                    optionsCallback: async(search: string) => this.$serviceContainer.userService.formOptionsCallback(search),
                    titlesCallback: async(values: string[]) => this.$serviceContainer.userService.formTitlesCallback(values),
                },

                {
                    key: 'type',
                    type: 'string',
                    title: this.$t('general.field.type').toString(),
                    hideTitle: true,
                    value: this.type,
                    required: true,
                    options: [
                        {
                            value: 'month',
                            title: 'Month',
                        },
                        {
                            value: 'week',
                            title: 'Week',
                        },
                        {
                            value: 'day',
                            title: 'Day',
                        },
                    ],
                    dropdown: true,
                },
            ],
        };

        this.form = Object.assign({}, form);
    }

    /**
     * Handles form value changes
     */

    private async onFormValuesChanged(values: FormFieldValuesInterface)
    {
        this.showTimesheets = values.timesheets as boolean;

        if (values.user && this.selectedUser?.id !== (values.user as string))
        {
            this.selectedUser = await this.$serviceContainer.userService.loadUser(values.user as string);

            if (this.selectedUser)
            {
                const query = Object.assign({}, this.$route.query);
                query.user_id = this.selectedUser.id;

                this.$router.replace({ query }).catch();

                this.loadEvents();
            }
        }

        this.type = values.type as string;
    }
}